import React from 'react';
import { Calendar, MapPin, Truck, CheckCircle2, User, Mail, Phone } from 'lucide-react';
import { Booking } from '../../types/booking';
interface ConfirmationProps {
  data: {
    startDate: string;
    endDate: string;
    method: 'pickup' | 'dropoff' | null;
    firstName?: string;
    surname?: string;
    farm?: string;
    email?: string;
    phone?: string;
  };
  existingBookings?: Booking[];
}
export function Confirmation({
  data,
  existingBookings = []
}: ConfirmationProps) {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const calculateDuration = () => {
    if (!data.startDate || !data.endDate) return '';
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };
  // Find who has the machine before the selected start date
  const getCurrentHolder = () => {
    if (!data.startDate || existingBookings.length === 0) return null;
    const selectedStartTime = new Date(data.startDate).getTime();
    const previousBooking = existingBookings.filter(booking => {
      if (booking.status === 'completed') return false;
      const [day, month, year] = booking.endDate.split('/');
      const bookingEndDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      const bookingEndTime = new Date(bookingEndDate).getTime();
      return bookingEndTime < selectedStartTime;
    }).sort((a, b) => {
      const [dayA, monthA, yearA] = a.endDate.split('/');
      const [dayB, monthB, yearB] = b.endDate.split('/');
      const dateA = new Date(`${yearA}-${monthA.padStart(2, '0')}-${dayA.padStart(2, '0')}`).getTime();
      const dateB = new Date(`${yearB}-${monthB.padStart(2, '0')}-${dayB.padStart(2, '0')}`).getTime();
      return dateB - dateA;
    })[0];
    return previousBooking || null;
  };
  const currentHolder = getCurrentHolder();
  return <div className="space-y-6" data-id="element-447">
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 text-center" data-id="element-448">
        <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-449">
          <CheckCircle2 size={24} data-id="element-450" />
        </div>
        <h3 className="text-lg font-bold text-emerald-900" data-id="element-451">Ready to Book</h3>
        <p className="text-emerald-700 text-sm mt-1" data-id="element-452">
          Please review your booking details below.
        </p>
      </div>

      <div className="space-y-4" data-id="element-453">
        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-454">
          <Calendar className="text-slate-400 mt-1 shrink-0" size={20} data-id="element-455" />
          <div className="flex-1" data-id="element-456">
            <h4 className="font-medium text-slate-900" data-id="element-457">Date & Duration</h4>
            <p className="text-slate-600 text-sm mt-0.5" data-id="element-458">
              {formatDate(data.startDate)} — {formatDate(data.endDate)}
            </p>
            <p className="text-slate-400 text-xs mt-1" data-id="element-459">{calculateDuration()}</p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-460">
          <Truck className="text-slate-400 mt-1 shrink-0" size={20} data-id="element-461" />
          <div className="flex-1" data-id="element-462">
            <h4 className="font-medium text-slate-900" data-id="element-463">Collection Method</h4>
            <p className="text-slate-600 text-sm mt-0.5" data-id="element-464">
              {data.method === 'pickup' ? 'Farm Pickup' : 'Collection at Stanthorpe WMF'}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-465">
          <User className="text-slate-400 mt-1 shrink-0" size={20} data-id="element-466" />
          <div className="flex-1" data-id="element-467">
            <h4 className="font-medium text-slate-900" data-id="element-468">Your Details</h4>
            <p className="text-slate-600 text-sm mt-0.5 font-medium" data-id="element-469">
              {data.firstName} {data.surname}
            </p>
            <p className="text-slate-500 text-sm" data-id="element-470">{data.farm}</p>
          </div>
        </div>

        <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-471">
          <Mail className="text-slate-400 mt-1 shrink-0" size={20} data-id="element-472" />
          <div className="flex-1" data-id="element-473">
            <h4 className="font-medium text-slate-900" data-id="element-474">Contact Information</h4>
            <p className="text-slate-600 text-sm mt-0.5" data-id="element-475">{data.email}</p>
            <p className="text-slate-500 text-sm" data-id="element-476">{data.phone}</p>
          </div>
        </div>

        {data.method === 'pickup' && currentHolder && <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-477">
            <User className="text-slate-400 mt-1 shrink-0" size={20} data-id="element-478" />
            <div className="flex-1" data-id="element-479">
              <h4 className="font-medium text-slate-900" data-id="element-480">Pickup From</h4>
              <p className="text-slate-600 text-sm mt-0.5 font-medium" data-id="element-481">
                {currentHolder.userName}
              </p>
              <p className="text-slate-500 text-sm" data-id="element-482">
                {currentHolder.location || currentHolder.farm}
              </p>
            </div>
          </div>}

        {data.method === 'dropoff' && <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-483">
            <MapPin className="text-slate-400 mt-1 shrink-0" size={20} data-id="element-484" />
            <div className="flex-1" data-id="element-485">
              <h4 className="font-medium text-slate-900" data-id="element-486">
                Collection Location
              </h4>
              <p className="text-slate-600 text-sm mt-0.5" data-id="element-487">
                Stanthorpe Waste Management Facility
              </p>
            </div>
          </div>}
      </div>

      <div className="text-xs text-slate-400 text-center px-4" data-id="element-488">
        By confirming, you agree to the machine usage terms and responsibility
        for maintenance during your booking period.
      </div>
    </div>;
}